import React, { useEffect, useMemo, useRef, useState } from "react";
import "./cartSection.css";
import ScannedCard from "../cards/scannedCard/ScannedCard";
import { Button, CircularProgress, Fab } from "@mui/material";
import {
  loadItemSizes,
  peekItemSizes,
  useGlobalContext,
} from "../../../context/GlobalContext";
import GiantModal from "../modals/giantModal/GiantModal";
import { GetScannedDesign } from "../emptyScreen/api/scanProductApi";
import ScannedCardSkeleton from "../skeletons/scannedCardSkeleton";
import { toast } from "react-hot-toast";
import CustomToast from "../CustomToast";
import { fetchUspandVideo } from "../../../features/home/api/HomeScreenApi";
import { DESIGN_SECTIONS } from "../../../features/cart/api/designStats";

const CartSection = () => {
  const {
    search,
    setScanned,
    setHomeScreen,
    cartData,
    handleRemoveFromCart,
    setDetails,
    setCartData,
    homeScreen,
    setSelectedCard,
    scrollRef,
    videoModal,
    setVideoModal,
  } = useGlobalContext();

  const [giantModal, setGiantModal] = useState();

  // Derived from cartData and search, so it is worked out during render rather
  // than pushed through state. Held in state, every cart change cost an extra
  // render pass of the whole list before the totals below could even run.
  const filteredCart = useMemo(() => {
    if (!search) return cartData;
    const needle = search.toString().toLowerCase();
    return cartData.filter((item) =>
      item.styleNumber?.toString().toLowerCase().includes(needle)
    );
  }, [search, cartData]);

  const [inputAdd, setInputAdd] = useState("");
  const [customLoading, setCustomLoading] = useState(false);
  const buffer = useRef("");

  useEffect(() => {
    setCustomLoading(true);
    const timer = setTimeout(() => setCustomLoading(false), 1000);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  useEffect(() => {
    if (cartData.length < 1) {
      localStorage.removeItem("scanned");
      setScanned(false);
      setHomeScreen(false);
    } else if (cartData.length >= 1) {
      localStorage.setItem("scanned", true);
      setScanned(true);
      setHomeScreen(true);
    }
  }, [cartData]);

  useEffect(() => {
    const diamondWeight = filteredCart.reduce(
      (acc, item) => acc + (item.diaWeight * item.quantity || 0),
      0
    );
    const goldWeight = filteredCart.reduce(
      (acc, item) => acc + (item.netWeight * item.quantity || 0),
      0
    );
    const designCount = filteredCart.length;
    const uniqueCategories = new Set(
      filteredCart.map((item) => item.categoryName)
    ).size;

    // Plain arithmetic on the cart, so it runs on the first render too. Gating
    // it behind the one second spinner left the totals at zero for a cart that
    // was already restored, and nothing re-ran the effect once the spinner
    // cleared - the header sat on its skeleton until the cart was touched.
    setDetails({
      diamondWeight,
      goldWeight,
      designCount,
      categories: uniqueCategories,
    });
  }, [filteredCart]);

  // Only on the way out. Clearing on every cart change blanked the header for a
  // frame before the new totals landed.
  useEffect(() => {
    return () => {
      setDetails({
        prevPurchase: 0,
        diamondWeight: 0,
        goldWeight: 0,
        designCount: 0,
        categories: 0,
        cartScore: 0,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setInputAdd("");
  }, [videoModal]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Accumulate characters
      if (e.key.length === 1 && e.key !== "Enter") {
        buffer.current += e.key;
        return;
      }

      // Handle Enter key
      if ((inputAdd || buffer.current !== "") && e.key === "Enter") {
        e.preventDefault();
        const isModalOpen = videoModal.isScanned || videoModal.isOpen;

        if (isModalOpen) {
          toast?.custom((t) => (
            <CustomToast
              t={t}
              message={"Close the modal to add a new design"}
              isError={true}
            />
          ));
          buffer.current = "";
          return;
        }

        const scannedCode = buffer.current.trim();
        if (scannedCode) {
          if (inputAdd === "") {
            // Nothing typed in the box, so this came off the scanner gun.
            handleAddDesign(scannedCode?.toUpperCase(), DESIGN_SECTIONS.SCAN);
          } else {
            handleAddDesign(inputAdd?.toUpperCase(), DESIGN_SECTIONS.SEARCH);
          }
        }
        buffer.current = "";
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [inputAdd, videoModal]);

  // section says how the design got here: typed into the box, or read off the
  // scanner. Stamped on the row so the order can be counted by section later.
  const handleAddDesign = async (design, section = DESIGN_SECTIONS.SEARCH) => {
    try {
      const response = await GetScannedDesign(design);
      if (response?.status !== 200) return;

      const categoryName = response?.data?.categoryName;
      const styleNumber = response?.data?.styleNumber;

      // Read the duplicate check from storage rather than cartData: this
      // function can be held in a keydown listener from an earlier render, so
      // its cartData may be stale, while cartItems is always current.
      const storedCart = JSON.parse(localStorage.getItem("cartItems")) || [];
      if (storedCart.some((item) => item?.styleNumber === styleNumber)) {
        toast.custom((t) => (
          <CustomToast t={t} isError={true} message="Design already added" />
        ));
        setInputAdd("");
        return;
      }

      // The size list is only read for the sizeAvailable flag, and the video
      // only feeds the popup, so neither has to hold up the card. Assume sizes
      // exist until confirmed: hasInvalidSize skips items whose sizeAvailable
      // is false, so guessing true keeps the PROCEED size check strict rather
      // than risking an unsized design slipping through.
      const cachedSizes = peekItemSizes(categoryName);
      const sizeAvailable = cachedSizes ? cachedSizes.length > 0 : true;

      const newItem = {
        ...response?.data,
        sizeAvailable,
        addSection: section,
        addedAt: new Date().toISOString(),
        quantityData: [
          {
            key: `${styleNumber}-${Date?.now()}`,
            itemSize:
              response?.data?.subCategoryName === "Without Chain"
                ? "N/A"
                : response?.data?.itemSize
                ? response?.data?.itemSize
                : "",
            goldColor: "P",
            goldQty: "18",
            diaQty: "VS-GH",
            message: null,
            rhodium: "W",
          },
        ],
        quantity: 1,
      };

      setCartData((prev) => {
        const existingItemIndex = prev?.findIndex(
          (item) => item?.styleNumber === newItem?.styleNumber
        );
        if (existingItemIndex >= 0) return prev;

        const updated = [...prev, newItem];
        setSelectedCard(styleNumber);
        localStorage.setItem("cartItems", JSON.stringify(updated));
        return updated;
      });

      toast.custom((t) => (
        <CustomToast t={t} message="Design added successfully" />
      ));

      if (scrollRef?.current) {
        scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }

      setInputAdd("");

      // Sizes and video depend only on the scan response, not on each other,
      // so they run together once the card is already on screen.
      const [sizes, videoData] = await Promise.all([
        cachedSizes !== undefined
          ? Promise.resolve(cachedSizes)
          : loadItemSizes(categoryName),
        fetchUspandVideo(styleNumber)
          .then((uspResponse) => uspResponse?.videoData || [])
          .catch((e) => {
            console.error("fetchUspandVideo failed:", e);
            return [];
          }),
      ]);

      if (videoData?.length > 0) {
        setVideoModal((prev) => ({
          ...prev,
          isScanned: true,
          data: videoData?.[0] || "",
        }));
      }

      const confirmed = sizes?.length > 0;
      if (confirmed !== sizeAvailable) {
        setCartData((prev) => {
          const updated = prev.map((item) =>
            item?.styleNumber === styleNumber
              ? { ...item, sizeAvailable: confirmed }
              : item
          );
          localStorage.setItem("cartItems", JSON.stringify(updated));
          return updated;
        });
      }
    } catch (error) {
      setInputAdd("");
      toast.custom((t) => (
        <CustomToast
          t={t}
          isError={true}
          message={error?.response?.data?.message || "Something went wrong"}
        />
      ));
    }
  };

  return (
    <>
      <div
        ref={scrollRef}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: "1vw",
          background: "#000000",
          width: "100%",
          height: "100%",
          flexGrow: 1,
          borderTop: ".1vw solid #0d0d0d",
          borderRight: homeScreen ? ".1vw solid #0d0d0d":".15vw solid #0d0d0d",
          padding: ".75vw  1.75vw",
          overflowY: "scroll",
          overflowX: "hidden",
        }}
      >
        {customLoading ? (
          <div
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress color="success" />
          </div>
        ) : (
          <>
            <input
              type="text"
              placeholder="Add by Design number or Jewelcode"
              value={inputAdd}
              onChange={(e) => {
                setInputAdd(e.target.value);
              }}
              className="custom-search-input"
            />
            {filteredCart
              .slice()
              .reverse()
              .map((style) => (
                <ScannedCard
                  key={style?._id}
                  data={style}
                  onDelete={() => handleRemoveFromCart(style)}
                />
              ))}
          </>
        )}
      </div>
      {!customLoading && homeScreen && (
        <Button
          sx={{
            width: "100%",
            height: "3vw",
            background: "#50C878",
            color: "#000000",
            fontFamily: "Poppins",
            fontSize: ".9vw",
            fontWeight: "600",
            padding: 0,
            minWidth: 0,
            borderRadius: 0,
            borderRight: "0.1vw solid #0d0d0d",
          }}
          onClick={() => {
            setGiantModal(true);
          }}
        >
          PROCEED
        </Button>
      )}
      <GiantModal
        openModal={giantModal}
        handleClose={() => setGiantModal(false)}
      />
    </>
  );
};

export default CartSection;

